/**
 * Spaces API - Section-space settings update (request)
 *
 * PATCH-semantics request body for a section-leader / admin updating a section
 * space's feature toggles. Every field is OPTIONAL: a request carries only the
 * toggles being changed, and an absent field leaves the persisted value
 * untouched.
 *
 * The editable toggles mirror the `settings` block on `SpaceDetailSchema`
 * (`SpaceSettingsSchema` in `./detail`) — minus the three intentionally
 * NON-editable fields:
 *   - `contactEnabled`        — a department feature, unused for sections.
 *   - `defaultShowInTimeline` — a per-user preference, not a space setting.
 *   - `defaultNotifyLevel`    — a per-user preference, not a space setting.
 * Those three are deliberately excluded from this contract: a leader/admin
 * editing a section space cannot set them.
 */

import { z } from 'zod'

// ============================================================================
// PATCH /v1/spaces/:spaceId/settings (section)
// ============================================================================

export const UpdateSpaceSectionSettingsSchema = z.object({
  chatEnabled: z.boolean().optional().describe('Whether the section space has chat enabled.'),
  mediaLibraryEnabled: z.boolean().optional().describe('Whether the section space exposes its media library.'),
  memberListEnabled: z.boolean().optional().describe('Whether members can view the section space roster.'),
  memberPostingEnabled: z.boolean().optional().describe('Whether non-leader members can post to the section space.'),
  memberMediaEnabled: z
    .boolean()
    .optional()
    .describe('Whether non-leader members can add media to existing albums in the space.'),
  commentsEnabled: z.boolean().optional().describe('Whether comments are enabled on section space posts.'),
  reactionsEnabled: z.boolean().optional().describe('Whether reactions are enabled on section space posts.'),
  sharingEnabled: z.boolean().optional().describe('Whether posts in the section space can be shared.')
})

export type UpdateSpaceSectionSettings = z.infer<typeof UpdateSpaceSectionSettingsSchema>

// ============================================================================
// Chat-toggle provisioning error contract (plan 004 U1/U3 — shared)
// ============================================================================

/**
 * The chat toggle (enable/disable) is an atomic provision-before-commit
 * operation (plan 004 KTD1). When it fails, the API error code carries one of
 * these stems so the client can branch RETRYABLE vs NON-RETRYABLE without
 * string-matching ad-hoc copy:
 *
 *  - `CHAT_UNAVAILABLE`        — NON-retryable. The space is structurally
 *    chat-incapable (a section with no linked PLATFORM_SECTION mirror group;
 *    R6a). `chatEnabled` is NOT committed. The client shows "Chat isn't
 *    available for this space" with NO retry.
 *  - `EXTERNAL_SERVICE_ERROR` — RETRYABLE. Stream provisioning/hide failed
 *    transiently (API blip / rate-limit / status write). `chatEnabled` is NOT
 *    committed. The client shows "Couldn't activate chat, try again" with Retry.
 *
 * The wire code is the endpoint-prefixed form (e.g. `SECTION_CHAT_UNAVAILABLE`,
 * `GROUP_SETTINGS_EXTERNAL_SERVICE_ERROR`); match on the SUFFIX/stem below so a
 * client is endpoint-agnostic across the community (`PATCH /groups/:id/settings`)
 * and section (`PUT /schools/:id/sections/:id`) toggle paths.
 */
export const SpaceChatProvisionErrorCode = {
  /** Non-retryable: space cannot host chat (no mirror group). */
  CHAT_UNAVAILABLE: 'CHAT_UNAVAILABLE',
  /** Retryable: transient Stream provisioning/hide failure. */
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR'
} as const

export type SpaceChatProvisionErrorCode = (typeof SpaceChatProvisionErrorCode)[keyof typeof SpaceChatProvisionErrorCode]

/**
 * True when an API error code (raw or endpoint-prefixed) is the non-retryable
 * "chat isn't available for this space" failure. Clients use this to suppress
 * the Retry affordance (R6a). Endpoint-agnostic — matches the stem so a prefixed
 * `SECTION_CHAT_UNAVAILABLE` is recognized.
 */
export function isChatUnavailableError(apiErrorCode: string | undefined | null): boolean {
  return typeof apiErrorCode === 'string' && apiErrorCode.endsWith(SpaceChatProvisionErrorCode.CHAT_UNAVAILABLE)
}
